/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import {Type} from "class-transformer";
import UploadInfoItem from "@/core/components/File/FileUploadProgress/UploadInfoItem";
import {humanFileSize} from "@/utils/helpers";
import dayjs from 'dayjs'
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import TagItem from "@shared/Tags/TagItem";
import {QwoachFileType} from "./QwoachFileType"
import FilterableItem from "@shared/Filterable/FilterableItem.js";

export default class QwoachFile extends FilterableItem{

	//for public read: https://firebasestorage.googleapis.com/v0/b/<bucketName>/o/<urlEncodedFilePath>?alt=media
	// const createPersistentDownloadUrl = (bucket, pathToFile, downloadToken) => {
	// 	return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(
	// 		pathToFile
	// 	)}?alt=media&token=${downloadToken}`;
	// };
	@FilterableProperty()
    name: string = ""
	name_casefolded: string = null //needed to support case insensitive sorting. ref:https://stackoverflow.com/a/48098215/1713920
	path:  string = ""
	url:  string = ""
	size: number = 0
	parentId: string = null
	ownerId: string = null
	@FilterableProperty({includeFullWord: true})
	mimeType: string = null
	isFolder: boolean = false
	isDeleted: boolean = false

	type: QwoachFileType = QwoachFileType.DRIVE_FILE

	@FilterableProperty(tag => tag.title)
	@Type(() => TagItem)
	tagItemsArray: TagItem[] = [];


	@Type(() => UploadInfoItem)
	_uploadInfo: UploadInfoItem = new UploadInfoItem()
	_file = null
	_isSelected : boolean = false


	constructor(name: string = null, path: string = null, url: string = null) {
		super();
		this.setName(name)
		this.path = path;
		this.url = url;
	}

	getCurrentSchemaVersion(){
		return 3
	}

	migrateSchema(oldVersion, newVersion) {
		if(oldVersion < 2){
			if(!this.mimeType){
				if(this.type === QwoachFileType.LINK){
					this.mimeType = "link"
				} else if(this.isFile()){
					this.mimeType = "doc"
				}
			}
			this.syncNameCaseFolded()
		}
		if(oldVersion < 3){
			let directAdsArray = []
			for(let i = 0; i < this.sharedWithIds.length; i++){
				let id = this.sharedWithIds[i]
				id = QwoachFile.asDirectSharedId(QwoachFile.asInderectSharedId(id))
				directAdsArray.push(id)
			}
			this.sharedWithIds = [...new Set([...this.sharedWithIds, ...directAdsArray])]
		}
		super.migrateSchema(oldVersion, newVersion)
	}


	setName(name = null){
		this.name = name;
		this.syncNameCaseFolded()
	}

	syncNameCaseFolded(){
		this.name_casefolded = this.caseFoldNormalize(this.name)
	}

	setParent(qwoachFile){
    	if(qwoachFile){
			this.parentId = qwoachFile.id
			// this.parentIdArray = [...qwoachFile.parentIdArray, qwoachFile.id]
			// this.parentNameArray = [...qwoachFile.parentNameArray, qwoachFile.name]
		}
	}

	isFile(){
		return !this.isFolder && !this.isLink()
	}

	isLink(){
		return this.type === QwoachFileType.LINK
	}

	moveToNewClient(oldClientId, newClientId){
		let sharedClientIdIndex = this.sharedWithIds.findIndex(id => id === oldClientId)

		if(sharedClientIdIndex < 0){
			return false
		}
		this.sharedWithIdsOld = this.sharedWithIdsOld || []
		this.sharedWithIdsOld.push(oldClientId)
		this.sharedWithIds[sharedClientIdIndex] = newClientId
		return true
	}

	getAbsoluteUrl(){
		if(!this.url){
			return
		}
		if(this.url.indexOf("://") > 0){
			return this.url
		}
		return `https://${this.url}`
	}

	setIsFolder(isFolder){
		this.isFolder = isFolder
		if(isFolder){
			this.mimeType = null
		}
	}

	sizeFormatted(){
		if (this.isFolder) {
			return "--"
		}
		return humanFileSize(this.size)
	}

	addSharedWithByIdInderect(ids) {
		if(ids){
			return super.addSharedWithById(ids.map(QwoachFile.asInderectSharedId));
		}
		return false
	}

	updatedTsFormatted(){
		let lastModifiedDate = dayjs(this.updatedAt || this.updatedTs)
		let hoursDiff = dayjs().diff(lastModifiedDate, 'hours', true)
		if (hoursDiff < 24) {
			return lastModifiedDate.format("hh:mm a")
		}
		return lastModifiedDate.format("MMM DD, YYYY")
	}

	isParentRootFolder(){
		return this.parentId === null
	}

	setUpdatedNow(){
    	this.updatedAt = new Date().getTime()
	}

	createStorageMetadata(){
    	return {
    		contentType: this.mimeType || undefined,
			customMetadata: {
    			ownerId: this.ownerId,
				fileId: this.id,
				name: this.name,
				name_casefolded: this.name_casefolded,
				mimeType: this.mimeType,
				parentId: this.parentId,
				isFolder: this.isFolder,
				isDeleted: this.isDeleted,
				sharedWithIdsArrayString: JSON.stringify(this.sharedWithIds)
			}
		}
	}

	// addAssignedTo(baseProfile){
	// 	let isAdded = this.assignedTo.pushUnique(new ProfileShort(baseProfile))
	// 	if(isAdded){
	// 		this.addSharedWithById(baseProfile.id)
	// 	}
	// }

	static createFolder(folderName = "Folder", parentFolder = null) {
		let folder = new QwoachFile(folderName)
		folder.setIsFolder(true)
		folder.setParent(parentFolder)
		return folder
	}

	//this id is used to show first level files in the root of the shared folder
	static asDirectSharedId(id){
		if(id.indexOf("direct_") < 0){
			id = "direct_" + id
		}
		return id
	}

	static asInderectSharedId(id){
		return id.replace("direct_", "")
	}



}
